import { AppType, useAppConfiguration } from "@domain/AppConfiguration";
import type { ILocation } from "@domain/General/Location";
import { useJupiterProps } from "@modules/Jupiter";
import { useUserSettings } from '@services/UserSettings';
import { isNullOrUndefined } from "is-what";

/**
 * Gps Co-ordinates.
 */
export type GpsCoordinates = {
    latitude: number,
    longitude: number
};

/**
 * The Gps Co-ordinates to base a location search off of with a limited distance.
 */
export type GpsSearchBoundary = ( GpsCoordinates & {
    distanceKms: number
} ) | undefined;

type GetCoordinatesResponse = GpsCoordinates | undefined;

/**
 * Gets a co-ordinates object based on the provided location.
 * @param location The location (such as dealer or service centre).
 */
const getLocationCoordinates = ( location: ILocation | undefined ): GetCoordinatesResponse =>
{
    if ( !isNullOrUndefined( location?.latitude ) && !isNullOrUndefined( location?.longitude ) )
    {
        return {
            latitude: location.latitude,
            longitude: location.longitude
        }
    }

    return undefined;
};

/**
 * Gets the search boundary object that can be posted to the server for limited offers based off the users current location.
 * @param location The users current location.
 * @param distanceKms The distance to limit the search to based on user preference.
 */
export const getSearchBoundary = ( location: GpsCoordinates | undefined, distanceKms: number ): GpsSearchBoundary =>
{
    if ( location === undefined )
    {
        return undefined;
    }
    
    return {
        ...location,
        distanceKms
    };
}

/**
 * Gets the users current location within a set time period.
 * @param timeout The number of milliseconds to allow to pass before giving up.
 * @returns A promise that will return the result of the users current location.
 */
export const getGpsCoordinatesWithTimeout = ( timeout: number ): Promise<GetCoordinatesResponse> =>
{
    return Promise.race( [
        getGpsCoordinates(),
        // If the co-ordinates still haven't been retrieved within three seconds, assume we aren't getting them in order to prevent the page from waiting.
        // The only reason why this timeout should be executed is because the app is asking for permission to get the current location. If it already has permission or if it already has been denied permission, then it will exit/resolve straight away.
        new Promise<GpsCoordinates | undefined>( resolve =>
        {
            setTimeout( () => resolve( undefined ), timeout );
        } )
    ] );
}

/**
 * Gets the users current location.
 * @returns A promise that will return the result of the users current location.
 */
export const getGpsCoordinates = (): Promise<GetCoordinatesResponse> =>
{
    const jupiterProps = useJupiterProps();
    const appConfiguration = useAppConfiguration();
    const userSettings = useUserSettings();

    const getAppConfigCoordinates: () => GetCoordinatesResponse = () =>
    {
        // If the app is for a dealership, use the dealer location.
        if ( appConfiguration.appType == AppType.Dealership )
        {
            return getLocationCoordinates( appConfiguration.dealerLocation );
        }

        // Likewise, if the app is for a service center, use the service location.
        if ( appConfiguration.appType == AppType.ServiceCenter )
        {
            return getLocationCoordinates( appConfiguration.serviceLocation );
        }

        // If the app type is for something else (not currently possible), return undefined. The App & API should always know how to handle undefined co-ordinates.
        return undefined;
    };

    return new Promise<GpsCoordinates | undefined>( resolve =>
    {
        // If custom (developer) GPS co-ordinates are on/defined, use those instead.
        if ( userSettings.enableCustomGpsCoordinates && userSettings.customGpsCoordinates )
        {
            resolve( { ...userSettings.customGpsCoordinates } );

            return;
        }

        // If Jupiter have provided a lat/long, use those co-ordinates.
        if ( !isNullOrUndefined( jupiterProps.gpsLat ) && !isNullOrUndefined( jupiterProps.gpsLong ) && !isNaN( jupiterProps.gpsLat ) && !isNaN( jupiterProps.gpsLong ) )
        {
            resolve( {
                latitude: jupiterProps.gpsLat,
                longitude: jupiterProps.gpsLong
            } );

            return;
        }

        // If the browser has access to the geolocation API, then try using it to get a real-time location.
        if (navigator.geolocation) 
        {
            navigator.geolocation.getCurrentPosition(( location ) =>
            {
                resolve ( {
                    longitude: location.coords.longitude,
                    latitude: location.coords.latitude
                } );
            }, () =>
            {
                // If an error occurs (such as denied access), fallback to the app config co-ordinates.
                resolve( getAppConfigCoordinates() );
            } );

            return;
        }

        // If the above fails, get the app config co-ordinates.
        resolve( getAppConfigCoordinates() );
    } );
};